import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { DomSanitizer, type SafeHtml } from '@angular/platform-browser';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import type {
  Comment,
  CommentAttachment,
  CommentMention,
  CommentUser,
} from '@tourfold/libraries/tf-api-client-public-v2';
import { renderCommentBody, renderCommentInlineText } from '../services/comment-body-renderer';
import { AttachmentsRowComponent } from './attachments-row.component';
import { CommentEditFormComponent } from './comment-edit-form.component';
import { MentionChipComponent } from './mention-chip.component';
import { ReactIconComponent } from './react-icon.component';
import { ReactionPickerComponent } from './reaction-picker.component';
import { ReactionsBarComponent } from './reactions-bar.component';

/**
 * Renders a single comment row. Used by `<tf-comments>` for both root comments and replies —
 * the parent decides indentation by wrapping replies in a CSS class. Threading depth is not
 * passed in here because v1 only renders one level of indentation regardless of depth.
 *
 * Tombstone view: when `comment().isDeleted`, the body is replaced with a translated
 * "deleted" notice (text + icon, never color-only — WCAG-AA). Author + timestamp still render
 * so the audit trail stays readable.
 *
 * Edited / moderated indicators sit beneath the body and translate at runtime.
 *
 * **F3 actions menu (the "…" button):**
 *  - **Edit** — visible when the current user is the author OR is a moderator. Toggles the row
 *    into edit mode (`editingId` is owned by the parent). Moderator-edit reveals a required
 *    reason field above the body textarea.
 *  - **Delete** — visible when the current user is the author OR is a moderator. The parent
 *    handles the confirmation dialog before issuing the soft-delete.
 *  - **Restore** — only on tombstones, moderator-only. The parent calls the restore endpoint.
 *  - **Edit history** — moderator-only, only when the row has been edited at least once.
 */
@Component({
  selector: 'tf-comment-row',
  standalone: true,
  imports: [
    DatePipe,
    TranslateModule,
    ButtonModule,
    AttachmentsRowComponent,
    CommentEditFormComponent,
    MentionChipComponent,
    ReactIconComponent,
    ReactionPickerComponent,
    ReactionsBarComponent,
  ],
  templateUrl: './comment-row.component.html',
  styleUrl: './comment-row.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.cmt]': 'true',
    '[class.cmt-reply]': 'isReply()',
    '[class.is-last-reply]': 'isLastReply()',
    '[class.has-replies]': 'hasReplies()',
    '[class.is-unread]': 'isVisuallyUnread()',
  },
})
export class CommentRowComponent {
  private readonly translate = inject(TranslateService);
  private readonly sanitizer = inject(DomSanitizer);

  readonly comment = input.required<Comment>();

  /** When true, render the "Reply" button. The parent gates this off in flat list mode. */
  readonly canReply = input<boolean>(false);

  /**
   * Thread-position flags drive the connector-line decoration in the SCSS — vertical trunk
   * under a root with `hasReplies`, vertical segment + horizontal stub on each reply, and
   * trim the trunk to the avatar center on `isLastReply`. Mirrors the prototype's
   * `.cmt.has-replies` / `.cmt-reply` / `.is-last-reply` selectors.
   */
  readonly isReply = input<boolean>(false);
  readonly isLastReply = input<boolean>(false);
  readonly hasReplies = input<boolean>(false);

  /**
   * Author of the parent comment when rendering the "in reply to @X" badge — flat mode only.
   * Threaded mode leaves this `null` because the connector lines already convey the
   * parent relationship visually. `null` also when the parent isn't on a currently-loaded
   * page; the row still renders but without the parent's name.
   */
  readonly replyToAuthor = input<CommentUser | null>(null);

  /**
   * Id of the currently authenticated user, or `null` when unknown. Drives the author-vs-
   * moderator gating on the row menu — Edit/Delete on a row whose `author.id === currentUserId`
   * are author actions and don't require {@link canModerate}.
   */
  readonly currentUserId = input<string | null>(null);
  /** Set when the calling user holds the `comments.moderate` grant. Gates moderator-only items. */
  readonly canModerate = input<boolean>(false);
  /** True when this row is the one currently being edited (parent owns the `editingId` signal). */
  readonly isEditing = input<boolean>(false);

  /** Emitted when the user clicks "Reply"; the parent toggles an inline composer. */
  readonly replyRequested = output<string>();
  /** "Edit" menu item — parent flips the row into edit mode (or out, on second click). */
  readonly editRequested = output<string>();
  /** "Delete" menu item — parent shows a confirm dialog and calls the soft-delete endpoint. */
  readonly deleteRequested = output<string>();
  /** "Restore" menu item — moderator-only, only on tombstones. */
  readonly restoreRequested = output<string>();
  /** "Edit history" menu item — moderator-only. */
  readonly viewHistoryRequested = output<string>();
  /** Inline edit form completed — parent merges the updated row into the in-memory list. */
  readonly editSaved = output<Comment>();
  /** Inline edit form cancelled — parent clears its `editingId` signal. */
  readonly editCancelled = output<void>();
  /**
   * Bubbles up the in-progress edit body so the host can surface the draft (e.g. AI page
   * context). The row only emits while the edit form is mounted; the parent maps incoming text
   * to the active `editingId`.
   */
  readonly editDraftChange = output<string>();
  /** Per-row mark-read action — only meaningful when the row is currently unread. */
  readonly markReadRequested = output<string>();
  /** Per-row mark-unread action — only meaningful when the row is currently read. */
  readonly markUnreadRequested = output<string>();
  /** "Copy link" menu item — parent builds the deep link URL and copies it to the clipboard. */
  readonly copyLinkRequested = output<string>();
  /**
   * Emitted when the user clicks an attachment chip on the row. The host (`<tf-comments>`) opens
   * the preview dialog or triggers a direct download depending on `attachment.isPreviewable`.
   * Inline images aren't in scope here; only the chip-row entries flow through this output.
   */
  readonly attachmentOpenRequested = output<CommentAttachment>();
  /**
   * Emitted when the user clicks a reaction chip or picks an emoji from the picker.
   * Payload is `{ commentId, emoji }`; the parent decides add vs remove based on the current
   * `youReacted` flag on the matching aggregate entry.
   */
  readonly reactionToggled = output<{ commentId: string; emoji: string }>();

  protected readonly authorLabel = computed<string>(() => {
    const a = this.comment().author;
    if (a.isDeleted) return ''; // template uses the translated "deleted user" string instead.
    return [a.firstName, a.lastName].filter(Boolean).join(' ');
  });

  protected readonly initials = computed<string>(() => {
    const a = this.comment().author;
    if (a.isDeleted) return '?';
    const f = a.firstName?.[0] ?? '';
    const l = a.lastName?.[0] ?? '';
    return (f + l).toUpperCase() || '?';
  });

  protected readonly isOwnComment = computed<boolean>(() => {
    const uid = this.currentUserId();
    return uid !== null && this.comment().author.id === uid;
  });

  /**
   * True when the moderator-only edit-reason field should be shown — i.e. the user is editing
   * someone else's comment under the moderate grant. An author editing their own comment uses
   * the same form without the reason field.
   */
  protected readonly editRequiresReason = computed<boolean>(
    () => !this.isOwnComment() && this.canModerate(),
  );

  protected readonly canEdit = computed<boolean>(
    () => !this.comment().isDeleted && (this.isOwnComment() || this.canModerate()),
  );
  protected readonly canDelete = computed<boolean>(
    () => !this.comment().isDeleted && (this.isOwnComment() || this.canModerate()),
  );
  protected readonly canRestore = computed<boolean>(
    () => this.comment().isDeleted && this.canModerate(),
  );
  protected readonly canViewHistory = computed<boolean>(
    () => this.canModerate() && this.comment().isEdited,
  );

  /**
   * Whether this row is unread *for the current user* and should render as such (left accent
   * bar + tinted background + "New" pill in the meta header). The user's own comments are
   * always treated as read locally — even if the backend somehow returned `isUnread: true` on
   * a row authored by the calling user (race during cursor advance, etc.) we suppress the
   * unread treatment because seeing your own post flagged "New" reads as a bug.
   */
  protected readonly isVisuallyUnread = computed<boolean>(
    () => this.comment().isUnread && !this.isOwnComment(),
  );

  /**
   * Mark-read / mark-unread actions render only on live rows the calling user did not author —
   * own comments are always read by definition, so flipping their state would be a no-op /
   * surprising. Both rely on `currentUserId` being set so we don't surface them for
   * unauthenticated previews.
   */
  protected readonly canMarkRead = computed<boolean>(
    () =>
      !this.comment().isDeleted
      && this.isVisuallyUnread()
      && this.currentUserId() !== null,
  );
  protected readonly canMarkUnread = computed<boolean>(
    () =>
      !this.comment().isDeleted
      && !this.comment().isUnread
      && !this.isOwnComment()
      && this.currentUserId() !== null,
  );

  /**
   * True when the actions toolbar should render — at least one inline action is available
   * (Reply, Edit, Delete, Restore, Edit history, Mark read/unread, Copy link). Copy link is
   * available on every row (live or tombstone) so the toolbar is effectively always visible
   * for rows the user can see, but we keep the gate for parity with the original "no toolbar
   * when nothing's clickable" design — `canCopyLink` is always true today, but a future
   * permission could disable it.
   */
  protected readonly canCopyLink = computed<boolean>(() => true);

  /**
   * True when the reactions bar should accept input. Live comments only; the calling user must
   * be authenticated. Tombstones still render existing reaction aggregates if the backend ever
   * surfaces them (today the server hides reactions on tombstones, so this gate just locks the
   * picker + chip interactivity).
   */
  protected readonly canReact = computed<boolean>(
    () => !this.comment().isDeleted && this.currentUserId() !== null,
  );

  /** True when the comment has at least one reaction — drives the visibility of the chip row. */
  protected readonly hasReactions = computed<boolean>(
    () => this.comment().reactions.length > 0,
  );

  /**
   * Shared picker for the row. Both entry points (the "React" button in the actions toolbar and
   * the "+" affordance inside the chip row when reactions already exist) call `toggle(event)` on
   * this one instance so the popover can anchor to the click target.
   */
  private readonly picker = viewChild<ReactionPickerComponent>('reactionPicker');

  protected onReactionToggled(emoji: string): void {
    this.reactionToggled.emit({ commentId: this.comment().id, emoji });
  }

  /**
   * Body broken into alternating text / mention segments. Each `@[name](type:uuid)` token in
   * `bodyMarkdown` becomes a `mention` segment; the surrounding markdown stays in `text`
   * segments that go through `<markdown>` as usual. The mention token's UUID is matched against
   * the server-resolved `comment().mentions` array so the chip shows the live display name and
   * deleted state — the inline token in the body is just a marker.
   *
   * Tombstones return an empty list — the body is already scrubbed server-side and we don't
   * render chips for deleted comments anyway. The template falls through to the tombstone
   * rendering path when `isDeleted` is true.
   */
  protected readonly bodySegments = computed<readonly CommentBodyDisplaySegment[]>(() => {
    const c = this.comment();
    if (c.isDeleted) return [];
    // Text runs go through the inline markdown pass so the serializer's hard-break backslashes
    // (`\` + newline) render as `<br>` and escaped punctuation is unescaped — otherwise the raw
    // markdown leaks visible backslashes between the chips. Pre-rendering here (rather than in a
    // template method) keeps the `SafeHtml` instance stable across change-detection cycles.
    return splitBodyIntoSegments(c.bodyMarkdown, c.mentions).map((segment) =>
      segment.kind === 'text'
        ? {
            kind: 'text' as const,
            html: this.sanitizer.bypassSecurityTrustHtml(renderCommentInlineText(segment.value)),
          }
        : segment,
    );
  });

  /**
   * Sanitized HTML for the comment body, used when there are no mentions to interleave. Goes
   * through the comments-specific renderer ({@link renderCommentBody}) instead of the global
   * `<markdown>` component because the latter inherits the AI assistant's sanitizer which strips
   * every `<img>`. Tombstones return `null` so the template falls through to the "deleted"
   * branch without paying for a useless marked.parse round-trip.
   */
  protected readonly renderedBodyHtml = computed<SafeHtml | null>(() => {
    const c = this.comment();
    if (c.isDeleted) return null;
    // `bypassSecurityTrustHtml` is mandatory here. `[innerHTML]` runs Angular's own DomSanitizer
    // as a second pass — and that sanitizer strips `style` attributes (among others), killing
    // the inline checkerboard / sizing styles we set on `<img>` post-DOMPurify. The body has
    // already been cleaned by `renderCommentBody` with a comments-specific allow-list, so
    // bypassing the Angular pass is safe and correct.
    return this.sanitizer.bypassSecurityTrustHtml(
      renderCommentBody(c.bodyMarkdown, c.attachments),
    );
  });

  /**
   * Delegated click handler on the rendered body. We listen on the host wrapper rather than
   * binding per-image so dynamically-rendered HTML doesn't need a re-walk on every render. When
   * the user clicks an `<img>` we match its `src` against the comment's inline attachments and
   * bubble an `attachmentOpenRequested` so the host can open the preview dialog.
   */
  protected onBodyClick(event: MouseEvent): void {
    this.activateBodyImage(event.target, event);
  }

  /**
   * Keyboard equivalent of {@link onBodyClick}. The renderer marks inline `<img>` with
   * `tabindex=0` + `role=button`, so Enter / Space on a focused image fires this handler. Other
   * keys propagate normally (Tab moves focus, arrow keys scroll, etc.).
   */
  protected onBodyKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    this.activateBodyImage(event.target, event);
  }

  private activateBodyImage(targetNode: EventTarget | null, event: Event): void {
    const target = targetNode as HTMLElement | null;
    if (target?.tagName !== 'IMG') return;
    // `data-attachment-id` is stamped by `renderCommentBody` once per render — see the
    // `decorateImages` walk. Reading it here is O(1); no URL-matching on the hot click path.
    const id = (target as HTMLImageElement).dataset['attachmentId'];
    if (!id) return;
    const match = this.comment().attachments.find(
      (a) => a.filestoreFileId.toLowerCase() === id.toLowerCase(),
    );
    if (!match) return;
    event.preventDefault();
    this.attachmentOpenRequested.emit(match);
  }

  protected onOpenReactionPicker(event: Event): void {
    this.picker()?.toggle(event);
  }

  protected onPickerEmojiSelected(emoji: string): void {
    this.reactionToggled.emit({ commentId: this.comment().id, emoji });
  }

  protected readonly hasActions = computed<boolean>(
    () =>
      (this.canReply() && !this.comment().isDeleted)
      || this.canEdit()
      || this.canDelete()
      || this.canRestore()
      || this.canViewHistory()
      || this.canMarkRead()
      || this.canMarkUnread()
      || this.canCopyLink(),
  );

  /**
   * Relative time string ("just now" / "5m ago" / "3h ago" / "2d ago" / "Mar 14").
   * Mirrors the prototype's `cmtRelTime` helper. Re-evaluates whenever `comment()` changes.
   * The absolute timestamp stays available as the `<time>` element's `title` for hover.
   */
  protected readonly relativeTime = computed<string>(() => {
    const created = this.comment().createdAt.getTime();
    const diffMin = Math.max(0, Math.floor((Date.now() - created) / 60_000));
    if (diffMin < 1) return this.translate.instant('COMMENTS.TIME.JUST_NOW');
    if (diffMin < 60) return this.translate.instant('COMMENTS.TIME.MINUTES', { count: diffMin });
    const diffHour = Math.floor(diffMin / 60);
    if (diffHour < 24) return this.translate.instant('COMMENTS.TIME.HOURS', { count: diffHour });
    const diffDay = Math.floor(diffHour / 24);
    if (diffDay < 7) return this.translate.instant('COMMENTS.TIME.DAYS', { count: diffDay });
    return this.comment().createdAt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  });
}

/**
 * Discriminated union of segment kinds emitted by [splitBodyIntoSegments]. The template iterates
 * over the result and chooses between `<markdown>` and `<tf-mention-chip>` per segment.
 */
export type CommentBodySegment =
  | { readonly kind: 'text'; readonly value: string }
  | { readonly kind: 'mention'; readonly mention: CommentMention };

/**
 * Template-facing variant of {@link CommentBodySegment}: text runs carry pre-rendered, sanitized
 * inline HTML (see {@link renderCommentInlineText}) instead of the raw markdown value, so the
 * template can bind them via `[innerHTML]`. Mention segments are unchanged.
 */
export type CommentBodyDisplaySegment =
  | { readonly kind: 'text'; readonly html: SafeHtml }
  | { readonly kind: 'mention'; readonly mention: CommentMention };

const MENTION_TOKEN_REGEX = /@\[([^\]]+)]\(([a-z]+):([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})\)/g;

/**
 * Pure helper exported for unit-testing. Walks [body] for mention tokens and emits alternating
 * text + mention segments. The mention's payload is taken from the matching entry in [mentions]
 * by `(type, id)` so the rendered chip reflects the live resolution (including the deleted
 * state). When the body contains a token that's not in [mentions] (shouldn't happen — the
 * backend persists tokens and resolved rows together — but defensive against drift), the token
 * stays in the text segment as-is.
 *
 * Code blocks (fenced + inline) are NOT specially masked here: the backend extractor already
 * skipped them at create/edit time, so any `@[name](type:uuid)` token inside a code block in
 * the stored body was never persisted as a mention row in the first place. Such tokens stay
 * literal in the rendered output.
 */
export function splitBodyIntoSegments(
  body: string,
  mentions: readonly CommentMention[],
): readonly CommentBodySegment[] {
  if (mentions.length === 0 || !body.includes('@[')) {
    return [{ kind: 'text', value: body }];
  }
  // Index mentions by the `<type>:<uuid>` token so the regex match can find its payload in O(1).
  const mentionByKey = new Map<string, CommentMention>();
  for (const m of mentions) {
    mentionByKey.set(`${m.type}:${m.id}`, m);
  }

  const segments: CommentBodySegment[] = [];
  let cursor = 0;
  // Reset lastIndex on every call since we declared the regex with `g` at module scope.
  MENTION_TOKEN_REGEX.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = MENTION_TOKEN_REGEX.exec(body)) !== null) {
    const type = match[2];
    const id = match[3];
    if (type === undefined || id === undefined) continue;
    const mention = mentionByKey.get(`${type}:${id.toLowerCase()}`);
    if (!mention) {
      // Token has no matching mention row (defensive — shouldn't happen). Leave it in the text.
      continue;
    }
    if (match.index > cursor) {
      segments.push({ kind: 'text', value: body.slice(cursor, match.index) });
    }
    segments.push({ kind: 'mention', mention });
    cursor = match.index + match[0].length;
  }
  if (cursor < body.length) {
    segments.push({ kind: 'text', value: body.slice(cursor) });
  }
  return segments;
}
